// src/components/SurgeryFormModal.tsx

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent, CloseButton, Button, Input, TextArea } from '../styles/GlobalStyles';
import { addSurgery, updateSurgery, getPatientsByInstitution, uploadFile } from '../services/firebase';
import { analyzePDFFiles, validateAPIKey } from '../services/pdfAnalysisService';
import { Surgery, Section, Patient, Attachment } from '../types';
import { useTranslation } from '../i18n';

interface SurgeryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSurgeryAdded: () => void;
  surgery?: Surgery | null;
}

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
`;

const PatientSearch = styled.div`
  width: 300px;
  border-right: 1px solid #e5e7eb;
  padding-right: 20px;
`;

const MainContent = styled.div`
  flex: 1;
  padding-left: 20px;
`;

const ContentArea = styled.div`
  display: flex;
  gap: 20px;
  max-height: 600px;
`;

const SectionContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const FileInput = styled.input`
  margin-top: 8px;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: #f3f4f6;
  border-radius: 4px;
  font-size: 12px;
`;

const PatientList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const PatientItem = styled.div<{ isSelected: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.isSelected ? '#0369a1' : '#e5e7eb'};
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  background-color: ${props => props.isSelected ? '#f0f9ff' : 'white'};
  
  &:hover {
    background-color: #f9fafb;
  }
`;

const SearchInput = styled(Input)`
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const AddSectionButton = styled(Button)`
  margin-top: 16px;
`;

const RemoveSectionButton = styled.button`
  color: #ef4444;
  font-size: 12px;
  padding: 4px 8px;
  background: none;
  border: none;
  cursor: pointer;
`;

// PDF 분석 관련 스타일들
const PDFAnalysisSection = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 16px 0px 16px;
  margin-bottom: 20px;
`;

const PDFAnalysisHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const PDFAnalysisTitle = styled.h3`
  margin: 0;
  color: #1e293b;
  font-size: 16px;
`;

const ToggleButton = styled.button`
  background: #e1e1ff;
  border: none;
  border-radius: 8px;
  color: #0369a1;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
`;

const APIKeyStatus = styled.div<{ isValid: boolean }>`
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 12px;
  
  ${props => props.isValid 
    ? 'background-color: #dcfce7; color: #166534;' 
    : 'background-color: #fecaca; color: #991b1b;'
  }
`;

const PDFUploadArea = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  margin-bottom: 12px;
  
  &.dragover {
    border-color: #0369a1;
    background-color: #f0f9ff;
  }
`;

const AnalysisStatus = styled.div<{ status: 'idle' | 'analyzing' | 'success' | 'error' }>`
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 16px;
  
  ${props => {
    switch (props.status) {
      case 'analyzing':
        return 'background-color: #fef3c7; color: #92400e;';
      case 'success':
        return 'background-color: #dcfce7; color: #166534;';
      case 'error':
        return 'background-color: #fecaca; color: #991b1b;';
      default:
        return 'display: none;';
    }
  }}
`;

const CustomInstructionsSection = styled.div`
  background-color: #fefbf6;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const CustomInstructionsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const CustomInstructionsTitle = styled.h4`
  margin: 0;
  color: #c2410c;
  font-size: 14px;
  font-weight: 600;
`;

const InstructionsTextArea = styled(TextArea)`
  background-color: #fffbeb;
  border-color: #fed7aa;
  min-height: 80px;
  font-size: 13px;
  
  &:focus {
    border-color: #fb923c;
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.1);
  }
  
  &::placeholder {
    color: #a16207;
  }
`;

const ExampleInstructions = styled.div`
  font-size: 12px;
  color: #a16207;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fef3c7;
  border-radius: 4px;
`;

const InstructionToggle = styled.button`
  background: none;
  border: none;
  color: #c2410c;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 2px;
  
  &:hover {
    background-color: #fed7aa;
  }
`;

const SurgeryFormModal: React.FC<SurgeryFormModalProps> = ({ isOpen, onClose, onSurgeryAdded, surgery }) => {
  const { t, language } = useTranslation();
  const institutionNumber = sessionStorage.getItem('institutionNumber') || '';

  const [surgeryName, setSurgeryName] = useState('');
  const [sections, setSections] = useState<Section[]>([{ id: '1', title: '', content: '', attachments: [] }]);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  
  const [showPDFAnalysis, setShowPDFAnalysis] = useState(false);
  const [apiKeyValid, setApiKeyValid] = useState<boolean | null>(null);
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'analyzing' | 'success' | 'error'>('idle');
  const [analysisMessage, setAnalysisMessage] = useState('');
  
  const [customInstructions, setCustomInstructions] = useState('');
  const [showInstructionExamples, setShowInstructionExamples] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPatients();
      checkAPIKey();
      if (surgery) {
        setSurgeryName(surgery.name);
        setSections(surgery.sections);
        setSelectedPatients(surgery.patientIds);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, surgery]);

  const checkAPIKey = async () => {
    try {
      const isValid = await validateAPIKey();
      setApiKeyValid(isValid);
    } catch (error) {
      setApiKeyValid(false);
    }
  };

  // 기관별 환자 목록 로드
  const loadPatients = async () => {
    try {
      const patientList = await getPatientsByInstitution(institutionNumber);
      setPatients(patientList);
    } catch (error) {
      console.error('환자 목록 로드 오류:', error);
    }
  };

  // 수정: 닉네임으로만 필터링
  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePatientSelect = (patientId: string) => {
    setSelectedPatients(prev => {
      if (prev.includes(patientId)) {
        return prev.filter(id => id !== patientId);
      } else {
        return [...prev, patientId];
      }
    });
  };

  const updateSection = (index: number, field: keyof Section, value: any) => {
    setSections(prev => prev.map((section, i) => 
      i === index ? { ...section, [field]: value } : section
    ));
  };

  const addSection = () => {
    setSections(prev => [...prev, {
      id: Date.now().toString(),
      title: '',
      content: '',
      attachments: []
    }]);
  };

  const removeSection = (index: number) => {
    if (sections.length > 1) {
      setSections(prev => prev.filter((_, i) => i !== index));
    }
  };

  // PDF 분석 관련 함수들
  const handlePDFUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');
      setPdfFiles(pdfFiles);
    }
  };

  const handlePDFDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files) {
      const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');
      setPdfFiles(pdfFiles);
    }
  };

  const handlePDFDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const analyzePDFs = async () => {
    if (!apiKeyValid) {
      setAnalysisStatus('error');
      setAnalysisMessage(t('surgeryForm.pdfAnalysis.status_error_no_key'));
      return;
    }
    
    setAnalysisStatus('analyzing');
    if (pdfFiles.length === 0) {
      setAnalysisMessage(t('surgeryForm.pdfAnalysis.status_generating_template'));
    } else {
      setAnalysisMessage(t('surgeryForm.pdfAnalysis.status_analyzing'));
    }
    
    try {
      const result = await analyzePDFFiles(
        pdfFiles,
        language,
        customInstructions.trim() || undefined
      );
      setSurgeryName(result.suggestedSurgeryName);
      setSections(result.sections);
      setAnalysisStatus('success');
      if (pdfFiles.length === 0) {
        setAnalysisMessage(t('surgeryForm.pdfAnalysis.status_template_generated', { count: result.sections.length }));
      } else {
        setAnalysisMessage(t('surgeryForm.pdfAnalysis.status_success', { count: result.sections.length }));
      }
    } catch (error) {
      console.error('PDF 분석 오류:', error);
      setAnalysisStatus('error');
      const errorMessage = error instanceof Error ? error.message : t('surgeryForm.pdfAnalysis.status_error_general');
      setAnalysisMessage(errorMessage);
    }
  };

  const handleFileUpload = async (sectionIndex: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    const sectionId = sections[sectionIndex].id;
    for (const file of fileArray) {
      if (file.size > 10 * 1024 * 1024) {
        alert(t('surgeryForm.alerts.fileTooLarge', { fileName: file.name }));
        return;
      }
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        alert(t('surgeryForm.alerts.unsupportedFileType', { fileName: file.name }));
        return;
      }
    }
    setUploadingFiles(prev => ({ ...prev, [sectionId]: true }));
    try {
      const attachments: Attachment[] = [];
      for (const file of fileArray) {
        const attachment = await uploadFile(file, `surgeries/${surgeryName || 'temp'}/sections/${sectionId}`);
        attachments.push(attachment);
      }
      updateSection(sectionIndex, 'attachments', [...(sections[sectionIndex].attachments || []), ...attachments]);
      alert(t('surgeryForm.alerts.uploadSuccess', { count: attachments.length }));
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      const errorMessage = error instanceof Error ? error.message : t('surgeryForm.alerts.unknownUploadError');
      alert(t('surgeryForm.alerts.uploadFailed', { error: errorMessage }));
    } finally {
      setUploadingFiles(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  const removeAttachment = (sectionIndex: number, attachmentId: string) => {
    const currentAttachments = sections[sectionIndex].attachments || [];
    const updatedAttachments = currentAttachments.filter(att => att.id !== attachmentId);
    updateSection(sectionIndex, 'attachments', updatedAttachments);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!surgeryName.trim()) {
      alert(t('surgeryForm.alerts.nameRequired'));
      return;
    }
    if (sections.some(section => !section.title.trim() || !section.content.trim())) {
      alert(t('surgeryForm.alerts.sectionRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      const surgeryData = {
        name: surgeryName,
        institutionNumber,
        patientIds: selectedPatients,
        sections: sections.filter(section => section.title.trim() && section.content.trim()),
        createdAt: surgery?.createdAt || new Date(),
        updatedAt: new Date()
      };
      if (surgery) {
        await updateSurgery(surgery.id, surgeryData);
      } else {
        await addSurgery(surgeryData);
      }
      onSurgeryAdded();
      handleClose();
    } catch (error) {
      console.error('수술 저장 오류:', error);
      alert(t('surgeryForm.alerts.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSurgeryName('');
    setSections([{ id: '1', title: '', content: '', attachments: [] }]);
    setSelectedPatients([]);
    setSearchTerm('');
    setShowPDFAnalysis(false);
    setApiKeyValid(null);
    setPdfFiles([]);
    setAnalysisStatus('idle');
    setAnalysisMessage('');
    setCustomInstructions('');
    setShowInstructionExamples(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()} style={{ width: '95vw', maxWidth: '1200px' }}>
        <CloseButton onClick={handleClose}>×</CloseButton>
        <h2 style={{ marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>
          {surgery ? t('surgeryForm.modalTitle_edit') : t('surgeryForm.modalTitle_add')}
        </h2>
        
        {!surgery && (
          <PDFAnalysisSection>
            <PDFAnalysisHeader>
              <PDFAnalysisTitle>{t('surgeryForm.pdfAnalysis.title')}</PDFAnalysisTitle>
              <ToggleButton type="button" onClick={() => setShowPDFAnalysis(!showPDFAnalysis)}>
                {showPDFAnalysis ? t('surgeryForm.pdfAnalysis.toggle_hide') : t('surgeryForm.pdfAnalysis.toggle_show')}
              </ToggleButton>
            </PDFAnalysisHeader>
            
            {showPDFAnalysis && (
              <div>
                <APIKeyStatus isValid={apiKeyValid ?? false}>
                  {apiKeyValid === null ? t('surgeryForm.pdfAnalysis.apiKey_checking') : apiKeyValid ? t('surgeryForm.pdfAnalysis.apiKey_valid') : t('surgeryForm.pdfAnalysis.apiKey_invalid')}
                </APIKeyStatus>
                <FormGroup>
                  <Label>{t('surgeryForm.pdfAnalysis.upload_label')}</Label>
                  <PDFUploadArea onDrop={handlePDFDrop} onDragOver={handlePDFDragOver}>
                    <input type="file" multiple accept=".pdf" onChange={handlePDFUpload} style={{ display: 'none' }} id="pdf-upload"/>
                    <label htmlFor="pdf-upload" style={{ cursor: 'pointer' }}>
                      <div>{t('surgeryForm.pdfAnalysis.dropzone_main')}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {t('surgeryForm.pdfAnalysis.dropzone_sub')}
                      </div>
                    </label>
                  </PDFUploadArea>
                  {pdfFiles.length > 0 && <div style={{ marginTop: '8px', fontSize: '14px' }}>{t('surgeryForm.pdfAnalysis.selected_files', { files: pdfFiles.map(f => f.name).join(', ') })}</div>}
                </FormGroup>
                
                <CustomInstructionsSection>
                  <CustomInstructionsHeader>
                    <CustomInstructionsTitle>{t('surgeryForm.pdfAnalysis.custom_instruction_title')}</CustomInstructionsTitle>
                    <InstructionToggle type="button" onClick={() => setShowInstructionExamples(!showInstructionExamples)}>
                      {showInstructionExamples ? t('surgeryForm.pdfAnalysis.example_toggle_hide') : t('surgeryForm.pdfAnalysis.example_toggle_show')}
                    </InstructionToggle>
                  </CustomInstructionsHeader>
                  <FormGroup>
                    <InstructionsTextArea value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)} placeholder={t('surgeryForm.pdfAnalysis.custom_instruction_placeholder')} rows={3}/>
                  </FormGroup>
                  {showInstructionExamples && (
                    <ExampleInstructions>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>{t('surgeryForm.pdfAnalysis.example_title')}</div>
                      <div>{t('surgeryForm.pdfAnalysis.example_1')}</div>
                      <div>{t('surgeryForm.pdfAnalysis.example_2')}</div>
                      <div>{t('surgeryForm.pdfAnalysis.example_3')}</div>
                      <div>{t('surgeryForm.pdfAnalysis.example_4')}</div>
                      <div>{t('surgeryForm.pdfAnalysis.example_5')}</div>
                    </ExampleInstructions>
                  )}
                </CustomInstructionsSection>
                
                <Button type="button" onClick={analyzePDFs} disabled={analysisStatus === 'analyzing' || !apiKeyValid} style={{ marginBottom: '12px' }}>
                  {analysisStatus === 'analyzing' ? 
                    (pdfFiles.length === 0 ? t('surgeryForm.pdfAnalysis.generating_button') : t('surgeryForm.pdfAnalysis.analyzing_button')) : 
                    (pdfFiles.length === 0 ? t('surgeryForm.pdfAnalysis.generate_button') : t('surgeryForm.pdfAnalysis.start_button'))
                  }
                </Button>
                <AnalysisStatus status={analysisStatus}>{analysisMessage}</AnalysisStatus>
              </div>
            )}
          </PDFAnalysisSection>
        )}
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="surgeryName">{t('surgeryForm.fields.surgeryName')}</Label>
            <Input id="surgeryName" type="text" value={surgeryName} onChange={(e) => setSurgeryName(e.target.value)} placeholder={t('surgeryForm.placeholders.surgeryName')}/>
          </FormGroup>

          <ContentArea>
            <PatientSearch>
              <Label>{t('surgeryForm.fields.patientSearch')}</Label>
              <SearchInput type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={t('surgeryForm.placeholders.patientSearch')} />
              <PatientList>
                {filteredPatients.map(patient => (
                  <PatientItem key={patient.id} isSelected={selectedPatients.includes(patient.id)} onClick={() => handlePatientSelect(patient.id)}>
                    <div style={{ fontWeight: '600' }}>{patient.name}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                      {t('dashboard.patientCard.nickname')}: {patient.name}
                    </div>
                  </PatientItem>
                ))}
              </PatientList>
            </PatientSearch>

            <MainContent>
              <Label>{t('surgeryForm.fields.sections')}</Label>
              {sections.map((section, index) => (
                <SectionContainer key={section.id}>
                  <SectionHeader>
                    <h4>{t('surgeryForm.section.title', { index: index + 1 })}</h4>
                    {sections.length > 1 && <RemoveSectionButton type="button" onClick={() => removeSection(index)}>{t('surgeryForm.section.remove')}</RemoveSectionButton>}
                  </SectionHeader>
                  
                  <FormGroup>
                    <Input type="text" value={section.title} onChange={(e) => updateSection(index, 'title', e.target.value)} placeholder={t('surgeryForm.section.title_placeholder')}/>
                  </FormGroup>
                  
                  <FormGroup>
                    <TextArea value={section.content} onChange={(e) => updateSection(index, 'content', e.target.value)} placeholder={t('surgeryForm.section.content_placeholder')} style={{ minHeight: '120px' }}/>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>{t('surgeryForm.section.attachments')}</Label>
                    <FileInput type="file" multiple accept="image/*,video/*" onChange={(e) => handleFileUpload(index, e.target.files)} disabled={uploadingFiles[section.id]}/>
                    {uploadingFiles[section.id] && <div>{t('surgeryForm.section.uploading')}</div>}
                    
                    {section.attachments && section.attachments.length > 0 && (
                      <AttachmentList>
                        {section.attachments.map(attachment => (
                          <AttachmentItem key={attachment.id}>
                            <span>{attachment.name}</span>
                            <button type="button" onClick={() => removeAttachment(index, attachment.id)} style={{ color: '#ef4444', background: 'none', border: 'none' }}>×</button>
                          </AttachmentItem>
                        ))}
                      </AttachmentList>
                    )}
                  </FormGroup>
                </SectionContainer>
              ))}
              
              <AddSectionButton type="button" onClick={addSection}>{t('surgeryForm.addSection')}</AddSectionButton>
              
              <ButtonGroup>
                <Button type="button" onClick={handleClose}>{t('common.cancel')}</Button>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? t('common.saving') : (surgery ? t('common.update') : t('common.add'))}
                </Button>
              </ButtonGroup>
            </MainContent>
          </ContentArea>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default SurgeryFormModal;